import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent } from 'react';

const DISMISS_DISTANCE_PX = 96;
const DISMISS_MIN_FLING_PX = 24;
const DISMISS_FLING_PX_PER_MS = 0.6;
const SETTLE_MS = 180;

/** A downward drag dismisses past a distance, or as a quick fling past a short one. */
export function shouldDismissSwipe(distancePx: number, velocityPxPerMs: number): boolean {
  return (
    distancePx >= DISMISS_DISTANCE_PX ||
    (distancePx >= DISMISS_MIN_FLING_PX && velocityPxPerMs >= DISMISS_FLING_PX_PER_MS)
  );
}

interface DragState {
  pointerId: number;
  sheet: HTMLElement;
  startY: number;
  lastY: number;
  lastT: number;
  velocity: number;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Swipe-down-to-dismiss for a bottom sheet, driven from its grabber and header.
 *
 * Spread the returned handlers on the drag region. The sheet is the nearest
 * `role="dialog"` ancestor, moved with an inline transform so no re-render runs
 * per frame. Presses on buttons inside the region stay ordinary clicks. The
 * close button and backdrop remain the accessible ways out; the swipe is the
 * touch shortcut a grabber promises.
 */
export function useSheetSwipeDismiss(onDismiss: () => void, enabled = true) {
  const drag = useRef<DragState | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!enabled || drag.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if ((event.target as HTMLElement).closest('button, a, input, textarea')) return;
      const sheet = event.currentTarget.closest<HTMLElement>('[role="dialog"]');
      if (!sheet) return;
      window.clearTimeout(timer.current);
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        pointerId: event.pointerId,
        sheet,
        startY: event.clientY,
        lastY: event.clientY,
        lastT: event.timeStamp,
        velocity: 0,
      };
      sheet.style.transition = 'none';
    },
    [enabled]
  );

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state || event.pointerId !== state.pointerId) return;
    const dt = event.timeStamp - state.lastT;
    if (dt > 0) state.velocity = (event.clientY - state.lastY) / dt;
    state.lastY = event.clientY;
    state.lastT = event.timeStamp;
    // The sheet follows the finger down and stops at rest going up.
    state.sheet.style.transform = `translateY(${Math.max(0, event.clientY - state.startY)}px)`;
  }, []);

  const finish = useCallback(
    (event: PointerEvent<HTMLElement>, cancelled: boolean) => {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;
      drag.current = null;
      const distance = Math.max(0, event.clientY - state.startY);
      const settle = prefersReducedMotion() ? 0 : SETTLE_MS;
      const { sheet } = state;
      sheet.style.transition = settle ? `transform ${settle}ms ease-out` : 'none';
      if (!cancelled && shouldDismissSwipe(distance, state.velocity)) {
        sheet.style.transform = 'translateY(100%)';
        timer.current = window.setTimeout(onDismiss, settle);
      } else {
        sheet.style.transform = 'translateY(0)';
        timer.current = window.setTimeout(() => {
          sheet.style.transition = '';
          sheet.style.transform = '';
        }, settle);
      }
    },
    [onDismiss]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: (event: PointerEvent<HTMLElement>) => finish(event, false),
    onPointerCancel: (event: PointerEvent<HTMLElement>) => finish(event, true),
  };
}
